import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { fetchTourSeenAt, markTourSeenOnServer } from './tour.api'
import { useAuthStore } from '@/features/auth/store/auth.store'
import { TOUR_STEPS } from './tourSteps'
import type { TourStep } from './types'

function isMobileViewport() {
  if (typeof window === 'undefined') return false
  return window.innerWidth < 640
}

function isStepTargetVisible(step: TourStep) {
  if (typeof window === 'undefined') return true

  const candidates = Array.from(document.querySelectorAll<HTMLElement>(`#${step.targetId}`))
  return candidates.some(el => {
    const rect = el.getBoundingClientRect()
    const style = window.getComputedStyle(el)
    return (
      rect.width > 0 &&
      rect.height > 0 &&
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      style.opacity !== '0'
    )
  })
}

function resolveVisibleSteps() {
  const visibleSteps = TOUR_STEPS.filter(isStepTargetVisible)
  return visibleSteps.length > 0 ? visibleSteps : TOUR_STEPS
}

const TOUR_SEEN_KEY_PREFIX = 'inmeet_tour_seen:'

function getTourSeenKey(email: string) {
  return `${TOUR_SEEN_KEY_PREFIX}${email.trim().toLowerCase()}`
}

interface TourContextType {
  isActive: boolean
  currentStepIndex: number
  currentStep: TourStep | null
  totalSteps: number
  startTour: () => void
  nextStep: () => void
  skipTour: () => void
  cancelTour: () => void
  resetTour: () => void
}

const TourContext = createContext<TourContextType | null>(null)

export function TourProvider({ children }: { children: React.ReactNode }) {
  const [isActive, setIsActive] = useState(false)
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [steps, setSteps] = useState<TourStep[]>(TOUR_STEPS)
  const userEmail = useAuthStore((s) => s.user?.email)
  // Account-level flag, read once the profile resolves.
  const [tourSeenAt, setTourSeenAt] = useState<string | null>(null)

  useEffect(() => {
    if (!userEmail) return
    let cancelled = false
    void fetchTourSeenAt().then((seen) => {
      if (!cancelled && seen) setTourSeenAt(seen)
    })
    return () => {
      cancelled = true
    }
  }, [userEmail])

  /**
   * Has this ACCOUNT seen the tour?
   *
   * The server's answer wins. localStorage is kept only as a fast path so the
   * tour does not flash before /users/me resolves — on its own it is
   * per-browser and per-origin, so skipping on one machine left the tour
   * waiting on the next, and clearing site data brought it back.
   */
  const hasSeenTour = useCallback(() => {
    if (typeof window === 'undefined') return true
    if (!userEmail) return true
    if (tourSeenAt) return true
    return window.localStorage.getItem(getTourSeenKey(userEmail)) === '1'
  }, [userEmail, tourSeenAt])

  const markTourSeen = useCallback(() => {
    // Local first, so the tour closes instantly and does not reappear if the
    // request is slow or fails.
    if (typeof window !== 'undefined' && userEmail) {
      window.localStorage.setItem(getTourSeenKey(userEmail), '1')
    }
    setTourSeenAt(new Date().toISOString())
    // Then the account, which is what makes it stick on another browser or
    // device. Fire-and-forget: a failure costs one extra showing, not a broken
    // dismissal.
    void markTourSeenOnServer()
  }, [userEmail])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isMobileViewport()) {
        setIsActive(false)
        return
      }

      if (hasSeenTour()) {
        setIsActive(false)
        return
      }

      const resolvedSteps = resolveVisibleSteps()
      setSteps(resolvedSteps)
      setCurrentStepIndex(0)
      setIsActive(resolvedSteps.length > 0)
    }, 900)
    return () => clearTimeout(timer)
  }, [hasSeenTour])

  const startTour = useCallback(() => {
    if (isMobileViewport()) {
      setIsActive(false)
      return
    }

    if (hasSeenTour()) {
      setIsActive(false)
      return
    }

    const resolvedSteps = resolveVisibleSteps()
    setSteps(resolvedSteps)
    setCurrentStepIndex(0)
    setIsActive(resolvedSteps.length > 0)
  }, [hasSeenTour])

  const nextStep = useCallback(() => {
    setCurrentStepIndex(prev => {
      if (prev >= steps.length - 1) {
        markTourSeen()
        setIsActive(false)
        return prev
      }
      return prev + 1
    })
  }, [markTourSeen, steps.length])

  const skipTour = useCallback(() => {
    markTourSeen()
    setIsActive(false)
  }, [markTourSeen])

  const cancelTour = useCallback(() => {
    markTourSeen()
    setIsActive(false)
  }, [markTourSeen])

  const resetTour = useCallback(() => {
    const resolvedSteps = resolveVisibleSteps()
    setSteps(resolvedSteps)
    setCurrentStepIndex(0)
    setIsActive(resolvedSteps.length > 0)
  }, [])

  useEffect(() => {
    if (!isActive || currentStepIndex < steps.length) return
    setCurrentStepIndex(0)
  }, [currentStepIndex, isActive, steps.length])

  return (
    <TourContext.Provider value={{
      isActive,
      currentStepIndex,
      currentStep: isActive ? steps[currentStepIndex] ?? null : null,
      totalSteps: steps.length,
      startTour,
      nextStep,
      skipTour,
      cancelTour,
      resetTour,
    }}>
      {children}
    </TourContext.Provider>
  )
}

export function useTour() {
  const ctx = useContext(TourContext)
  if (!ctx) throw new Error('useTour must be used within TourProvider')
  return ctx
}
